import { useEffect, useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import CountUp from "react-countup";
import {
    alpha,
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Grid,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import PostAddOutlinedIcon from "@mui/icons-material/PostAddOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import GradingOutlinedIcon from "@mui/icons-material/GradingOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { getAllSclasses } from "../../redux/sclassRelated/sclassHandle";
import { getAllStudents } from "../../redux/studentRelated/studentHandle";
import { getAllTeachers } from "../../redux/teacherRelated/teacherHandle";
import { getFeeSummary } from "../../redux/feeRelated/feeHandle";
import { useDashboardFeatures } from "../../hooks/useDashboardFeatures";

const UNIVERSITY_HERO_ACTIONS = [
    { to: "/Admin/addclass", label: "Add class", variant: "contained" },
    { to: "/Admin/classes", label: "Manage programmes", variant: "outlined" },
];

function KpiCard({ title, value, icon: Icon, color = "primary", suffix = "", hint }) {
    return (
        <Card
            elevation={0}
            sx={(t) => {
                const main = t.palette[color]?.main ?? t.palette.primary.main;
                const dark = t.palette[color]?.dark ?? main;
                return {
                    height: "100%",
                    borderRadius: 3,
                    border: `1px solid ${alpha(t.palette.divider, 0.9)}`,
                    background: `linear-gradient(135deg, ${dark} 0%, ${main} 100%)`,
                    color: t.palette.common.white,
                };
            }}
        >
            <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                        <Typography variant="overline" sx={{ opacity: 0.92, letterSpacing: 1 }}>
                            {title}
                        </Typography>
                        <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5, lineHeight: 1.1 }}>
                            <CountUp end={Number(value) || 0} duration={1.8} separator="," preserveValue />
                            {suffix}
                        </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: alpha("#fff", 0.18), color: "inherit", width: 46, height: 46 }}>
                        <Icon />
                    </Avatar>
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 2 }}>
                    <TrendingUpIcon sx={{ fontSize: 18, opacity: 0.9 }} />
                    <Typography variant="caption" sx={{ opacity: 0.92 }}>
                        {hint}
                    </Typography>
                </Stack>
            </CardContent>
        </Card>
    );
}

const QUICK_LINKS = [
    { to: "/Admin/addstudents", label: "Add Student", sub: "Enrol a learner", Icon: PersonAddAlt1OutlinedIcon, color: "primary" },
    { to: "/Admin/addclass", label: "Add Class", sub: "New programme cohort", Icon: PostAddOutlinedIcon, color: "secondary" },
    { to: "/Admin/classes", label: "Manage Classes", sub: "Years & semesters", Icon: MenuBookOutlinedIcon, color: "info" },
    { to: "/Admin/subjects/chooseclass", label: "Subjects", sub: "Curriculum tree", Icon: AccountTreeOutlinedIcon, color: "info" },
    { to: "/Admin/fees/collection", label: "Fee Collection", sub: "Record payments", Icon: PaymentsOutlinedIcon, color: "success" },
    { to: "/Admin/classes/results/overview", label: "View Results", sub: "Marks & GPA", Icon: GradingOutlinedIcon, color: "warning" },
];

function QuickLinkCard({ to, label, sub, Icon, color = "primary" }) {
    return (
        <Card
            component={RouterLink}
            to={to}
            elevation={0}
            sx={(t) => {
                const main = t.palette[color]?.main ?? t.palette.primary.main;
                return {
                    textDecoration: "none",
                    color: "inherit",
                    height: "100%",
                    borderRadius: 3,
                    border: `1px solid ${alpha(t.palette.divider, 0.88)}`,
                    bgcolor: t.palette.background.paper,
                    position: "relative",
                    overflow: "hidden",
                    transition: t.transitions.create(["transform", "box-shadow", "border-color"], { duration: 200 }),
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background: `linear-gradient(90deg, ${main}, ${alpha(main, 0.55)})`,
                    },
                    "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: `0 14px 36px ${alpha(main, 0.22)}`,
                        borderColor: alpha(main, 0.4),
                        "& .quick-link-arrow": {
                            opacity: 1,
                            transform: "translateX(4px)",
                        },
                    },
                };
            }}
        >
            <CardContent sx={{ pt: 2.75, pb: 2, px: 2.25, height: "100%" }}>
                <Stack spacing={1.5} justifyContent="space-between" sx={{ height: "100%", minHeight: 108 }}>
                    <Avatar
                        variant="rounded"
                        sx={{
                            width: 52,
                            height: 52,
                            bgcolor: (t) => alpha(t.palette[color]?.main || t.palette.primary.main, 0.12),
                            color: (t) => t.palette[color]?.main || t.palette.primary.main,
                        }}
                    >
                        <Icon />
                    </Avatar>
                    <Box>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.25 }}>
                            {label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.35 }}>
                            {sub}
                        </Typography>
                    </Box>
                    <ArrowForwardOutlinedIcon
                        className="quick-link-arrow"
                        sx={{
                            alignSelf: "flex-end",
                            fontSize: 20,
                            color: `${color}.main`,
                            opacity: 0.35,
                            transition: (t) => t.transitions.create(["opacity", "transform"], { duration: 200 }),
                        }}
                    />
                </Stack>
            </CardContent>
        </Card>
    );
}

const YEAR_ORDER = ["Year1", "Year2", "Year3", "Year4", "Year5"];

const UniversityHomePage = () => {
    const dispatch = useDispatch();
    const { studentsList } = useSelector((state) => state.student);
    const { sclassesList } = useSelector((state) => state.sclass);
    const { teachersList } = useSelector((state) => state.teacher);
    const { currentUser } = useSelector((state) => state.user);
    const { feeSummary } = useSelector((state) => state.fee);
    const { canShowModule, filterLinks } = useDashboardFeatures();

    const adminID = currentUser?._id;
    const showStudents = canShowModule("students");
    const showSetup = canShowModule("setup");
    const showFees = canShowModule("fees");

    useEffect(() => {
        if (!adminID) return;
        if (showStudents) dispatch(getAllStudents(adminID));
        if (showSetup) {
            dispatch(getAllSclasses(adminID, "Sclass"));
            dispatch(getAllTeachers(adminID));
        }
        if (showFees) dispatch(getFeeSummary(adminID));
    }, [adminID, dispatch, showStudents, showSetup, showFees]);

    const universityClasses = useMemo(() => {
        const all = Array.isArray(sclassesList) ? sclassesList : [];
        return all.filter((item) => (item.educationLevel || "Secondary") === "University");
    }, [sclassesList]);

    const universityStudents = useMemo(() => {
        const all = Array.isArray(studentsList) ? studentsList : [];
        return all.filter((item) => (item.educationLevel || item.sclass?.educationLevel || "Secondary") === "University");
    }, [studentsList]);

    const lecturersCount = useMemo(() => (Array.isArray(teachersList) ? teachersList.length : 0), [teachersList]);

    const programmesCount = useMemo(() => {
        return new Set(universityClasses.map((item) => String(item.programme || "").trim()).filter(Boolean)).size;
    }, [universityClasses]);

    const facultiesCount = useMemo(() => {
        return new Set(universityClasses.map((item) => String(item.faculty || "").trim()).filter(Boolean)).size;
    }, [universityClasses]);

    const activeAcademicYear = useMemo(() => {
        return universityClasses.find((item) => item.academicYear)?.academicYear || currentUser?.academicYearStart || "Current session";
    }, [universityClasses, currentUser]);

    const currentSemester = useMemo(() => {
        return universityClasses.find((item) => item.semester)?.semester || "Semester-based";
    }, [universityClasses]);

    const yearDistribution = useMemo(() => {
        const counts = YEAR_ORDER.reduce((acc, year) => ({ ...acc, [year]: 0 }), {});
        universityClasses.forEach((item) => {
            const year = item.levelCode;
            if (counts[year] !== undefined) {
                counts[year] += 1;
            }
        });
        return YEAR_ORDER.map((year) => ({ year, classes: counts[year] || 0 }));
    }, [universityClasses]);

    const facultyDistribution = useMemo(() => {
        const counts = {};
        universityClasses.forEach((item) => {
            const key = String(item.faculty || "Unassigned").trim() || "Unassigned";
            counts[key] = (counts[key] || 0) + 1;
        });
        return Object.entries(counts)
            .map(([faculty, classes]) => ({ faculty, classes }))
            .sort((a, b) => b.classes - a.classes)
            .slice(0, 6);
    }, [universityClasses]);

    const schoolLabel = currentUser?.schoolName || "University";
    const heroActions = useMemo(() => filterLinks(UNIVERSITY_HERO_ACTIONS), [filterLinks]);
    const quickLinks = useMemo(() => filterLinks(QUICK_LINKS), [filterLinks]);

    return (
        <Box
            sx={{
                minHeight: "100%",
                pb: 4,
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.secondary.main, 0.06)} 0%, ${t.palette.grey[100]} 24%)`
                        : alpha(t.palette.common.black, 0.18),
            }}
        >
            <Container maxWidth="xl" sx={{ pt: { xs: 2, sm: 3 } }}>
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2.5, sm: 3.5 },
                        mb: 3,
                        borderRadius: 3,
                        color: "common.white",
                        background: (t) =>
                            `linear-gradient(115deg, ${t.palette.secondary.dark} 0%, ${t.palette.secondary.main} 50%, ${alpha(
                                t.palette.primary.main,
                                0.88
                            )} 100%)`,
                        boxShadow: (t) => `0 16px 48px ${alpha(t.palette.secondary.main, 0.24)}`,
                    }}
                >
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" alignItems={{ md: "center" }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar
                                sx={{
                                    width: 58,
                                    height: 58,
                                    bgcolor: alpha("#fff", 0.18),
                                    color: "common.white",
                                }}
                            >
                                <SchoolOutlinedIcon fontSize="large" />
                            </Avatar>
                            <Box>
                                <Typography variant="overline" sx={{ opacity: 0.92, letterSpacing: 1.2 }}>
                                    University Dashboard
                                </Typography>
                                <Typography variant="h4" fontWeight={800} sx={{ lineHeight: 1.15 }}>
                                    {schoolLabel}
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.92, maxWidth: 640, mt: 0.5 }}>
                                    Semester-based academic oversight with programme tracking, faculty structure, GPA-ready results flows,
                                    and tuition collection in one place.
                                </Typography>
                            </Box>
                        </Stack>
                        {heroActions.length > 0 ? (
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {heroActions.map((action) => (
                                    <Button
                                        key={action.to}
                                        variant={action.variant}
                                        component={RouterLink}
                                        to={action.to}
                                        sx={
                                            action.variant === "contained"
                                                ? {
                                                      bgcolor: "common.white",
                                                      color: "secondary.main",
                                                      fontWeight: 700,
                                                      "&:hover": { bgcolor: alpha("#fff", 0.92) },
                                                  }
                                                : {
                                                      borderColor: alpha("#fff", 0.65),
                                                      color: "common.white",
                                                      fontWeight: 600,
                                                  }
                                        }
                                    >
                                        {action.label}
                                    </Button>
                                ))}
                            </Stack>
                        ) : null}
                    </Stack>
                </Paper>

                {(showStudents || showSetup || showFees) && (
                <Grid container spacing={2.5} sx={{ mb: 3 }}>
                    {showStudents ? (
                    <Grid item xs={12} sm={6} lg={3}>
                        <KpiCard
                            title="University students"
                            value={universityStudents.length}
                            icon={GroupsOutlinedIcon}
                            color="secondary"
                            hint="Students linked to university classes"
                        />
                    </Grid>
                    ) : null}
                    {showSetup ? (
                    <>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KpiCard
                            title="Lecturers"
                            value={lecturersCount}
                            icon={SchoolOutlinedIcon}
                            color="info"
                            hint="Teaching staff available this session"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <KpiCard
                            title="Programmes"
                            value={programmesCount}
                            icon={MenuBookOutlinedIcon}
                            color="primary"
                            hint="Distinct programmes in active classes"
                        />
                    </Grid>
                    </>
                    ) : null}
                    {showFees ? (
                    <Grid item xs={12} sm={6} lg={3}>
                        <KpiCard
                            title="Fee collected"
                            value={feeSummary ? Number(feeSummary.totalCollected) : 0}
                            icon={PaymentsOutlinedIcon}
                            color="success"
                            hint={feeSummary ? `${feeSummary.collectionRate} collection rate this term` : "Loading fee summary"}
                        />
                    </Grid>
                    ) : null}
                </Grid>
                )}

                {(showSetup || quickLinks.length > 0) && (
                <Grid container spacing={2.5} sx={{ mb: 3 }}>
                    {showSetup ? (
                    <Grid item xs={12} lg={4}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2.25,
                                borderRadius: 3,
                                height: "100%",
                                border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                            }}
                        >
                            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                                <Avatar sx={{ bgcolor: (t) => alpha(t.palette.secondary.main, 0.12), color: "secondary.main" }}>
                                    <ApartmentOutlinedIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight={700}>
                                        Academic Structure
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        University-specific setup
                                    </Typography>
                                </Box>
                            </Stack>
                            <Stack spacing={1.25}>
                                <Typography variant="body2">
                                    <strong>Academic year:</strong> {activeAcademicYear}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Current semester:</strong> {currentSemester}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Faculties configured:</strong> {facultiesCount}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Assessment model:</strong> CA 40% + Exam 60%
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Grading:</strong> GPA 4.0 scale
                                </Typography>
                            </Stack>
                        </Paper>
                    </Grid>
                    ) : null}
                    {quickLinks.length > 0 ? (
                    <Grid item xs={12} lg={showSetup ? 8 : 12}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2.25,
                                borderRadius: 3,
                                border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                            }}
                        >
                            <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
                                Quick Links
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Frequent university admin tasks
                            </Typography>
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "1fr",
                                        sm: "repeat(2, 1fr)",
                                        md: "repeat(3, 1fr)",
                                    },
                                    gap: 2,
                                }}
                            >
                                {quickLinks.map((link) => (
                                    <QuickLinkCard key={`${link.to}-${link.label}`} {...link} />
                                ))}
                            </Box>
                            {showSetup && universityClasses.length === 0 ? (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    No university classes are configured yet. Create Year 1-5 classes with programme, faculty, and semester details
                                    to unlock the full university dashboard.
                                </Alert>
                            ) : null}
                        </Paper>
                    </Grid>
                    ) : null}
                </Grid>
                )}

                {showSetup && (
                <Grid container spacing={2.5}>
                    <Grid item xs={12} lg={6}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2.5,
                                borderRadius: 3,
                                height: 360,
                                border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                            }}
                        >
                            <Typography variant="h6" fontWeight={700}>
                                Classes by Study Year
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                Distribution of university classes from Year 1 to Year 5
                            </Typography>
                            <Box sx={{ width: "100%", height: 260 }}>
                                <ResponsiveContainer>
                                    <BarChart data={yearDistribution}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} strokeOpacity={0.35} />
                                        <XAxis dataKey="year" tick={{ fontSize: 12 }} />
                                        <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                                        <Tooltip formatter={(value) => [`${value}`, "Classes"]} />
                                        <Bar dataKey="classes" fill="#7b1fa2" radius={[6, 6, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2.5,
                                borderRadius: 3,
                                height: 360,
                                border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                            }}
                        >
                            <Typography variant="h6" fontWeight={700}>
                                Faculties by Class Count
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                Top faculty buckets currently represented in the timetable
                            </Typography>
                            <Box sx={{ width: "100%", height: 260 }}>
                                <ResponsiveContainer>
                                    <BarChart data={facultyDistribution} layout="vertical" margin={{ left: 8 }}>
                                        <CartesianGrid strokeDasharray="3 3" horizontal={false} strokeOpacity={0.35} />
                                        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                                        <YAxis type="category" dataKey="faculty" width={120} tick={{ fontSize: 11 }} />
                                        <Tooltip formatter={(value) => [`${value}`, "Classes"]} />
                                        <Bar dataKey="classes" fill="#1976d2" radius={[0, 6, 6, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
                )}
            </Container>
        </Box>
    );
};

export default UniversityHomePage;
